import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Calendar, MapPin, Trophy, Users, Coins, ShieldCheck } from "lucide-react";
import AppLayout from "../components/AppLayout";
import Bracket from "../components/Bracket";
import ResultDialog from "../components/ResultDialog";
import EventShareDialog from "../components/EventShareDialog";
import InvitePlayersDialog from "../components/InvitePlayersDialog";
import AddToCalendarButton from "../components/AddToCalendarButton";
import AttendanceWidget from "../components/AttendanceWidget";
import GolfScorecard from "../components/GolfScorecard";
import GolfLeaderboard from "../components/GolfLeaderboard";
import RyderCupRounds from "../components/RyderCupRounds";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";

export default function TournamentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  const [tour, setTour] = useState(null);
  const [openDlg, setOpenDlg] = useState(null);
  const [scoreSignal, setScoreSignal] = useState(0);

  const fetchTour = () => api.get(`/tournaments/${id}`).then((r) => setTour(r.data));
  useEffect(() => { fetchTour(); }, [id]); // eslint-disable-line

  if (!tour) return <AppLayout><div className="pp-card p-10 text-center text-zinc-500">Loading...</div></AppLayout>;

  const isReg = user && tour.registrations?.some((r) => r.user_id === user.id);
  const isOrganizer = user && (tour.organizer_id === user.id || ["admin", "federation"].includes(user.role));
  const isGolf = tour.sport?.slug === "golf" && !!tour.golf;
  const playersById = Object.fromEntries((tour.registrations || []).map((r) => [r.user_id, r.user]));
  const namify = (pid) => playersById[pid] ? `${playersById[pid].first_name || ""} ${playersById[pid].last_name || ""}`.trim() : "Player";

  const reg = async (classId = null, opts = {}) => {
    if (!user) { navigate("/login"); return; }
    try {
      const res = await api.post(`/tournaments/${id}/register`,
        classId ? { class_id: classId, ...(opts.waitlist ? { waitlist: true } : {}) } : {});
      const status = res.data?.status;
      if (status === "waitlisted") toast.success(`Added to waitlist · #${res.data.position}`);
      else toast.success(t("register_now"));
      fetchTour();
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      // Server tells us "pass waitlist=true" when class is full → offer it inline
      if (e.response?.status === 409 && /full/i.test(String(detail)) && classId && !opts.waitlist) {
        if (window.confirm(`${detail}\n\nJoin the waitlist instead?`)) {
          await reg(classId, { waitlist: true });
          return;
        }
      }
      toast.error(formatApiError(detail) || e.message);
    }
  };
  const unregister = async (classId = null) => {
    if (!window.confirm("Unregister from this class? Your spot will go to the next waitlisted player.")) return;
    try {
      const res = await api.delete(`/tournaments/${id}/register`,
        { data: classId ? { class_id: classId } : {} });
      if (res.data?.promoted) toast.success("You unregistered — next waitlisted player has been promoted.");
      else toast.success("Unregistered");
      fetchTour();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  // Per-class registration tracking — preserves waitlist status & position
  const myRegByClass = {};
  for (const r of tour.registrations || []) {
    if (r.user_id === user?.id && r.class_id) myRegByClass[r.class_id] = r;
  }
  const hasClasses = (tour.classes || []).length > 0;
  const generateBracket = async () => {
    try { await api.post(`/tournaments/${id}/bracket/generate`); toast.success("Bracket generated"); fetchTour(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const recordBracket = async (round, idx, score_a, score_b) => {
    try { await api.post(`/tournaments/${id}/bracket/result`, { round, idx, score_a, score_b }); toast.success("Saved"); fetchTour(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <AppLayout>
      <Link to="/tournaments" data-testid="back-tournaments" className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1 mb-6"><ArrowLeft size={14} /> {t("tournaments")}</Link>
      {tour.cover_url && (
        <div className="rounded-2xl overflow-hidden mb-4 max-h-72" data-testid="tour-cover">
          <img src={tour.cover_url} alt={`${tour.title} cover`} className="w-full h-auto object-cover" />
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 pp-card-elevated p-8">
          <div className="text-xs uppercase tracking-wider flex items-center gap-1" style={{ color: tour.brand_color || "#007AFF" }}>
            <Trophy size={14} /> {tour.sport?.name}
            {tour.is_verified ? <span className="ms-2 inline-flex items-center gap-1 text-[#00E676]"><ShieldCheck size={12} /> Verified</span> : null}
            {tour.visibility === "private" && <span className="ms-2 pp-chip text-[10px] text-[#FFD60A] border-[#FFD60A]/40" data-testid="tour-private-badge">Private</span>}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black mt-2" data-testid="tour-title">{tour.title}</h1>
          <p className="text-zinc-400 mt-3">{tour.description}</p>
          <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
            <Info icon={<Calendar size={14} />} label={t("starts")} value={tour.all_day ? new Date(tour.start_date).toLocaleDateString() + " · All day" : new Date(tour.start_date).toLocaleString()} />
            <Info icon={<Calendar size={14} />} label={t("ends")} value={tour.all_day ? new Date(tour.end_date).toLocaleDateString() + " · All day" : new Date(tour.end_date).toLocaleString()} />
            <Info icon={<MapPin size={14} />} label={t("location")} value={tour.club?.name || tour.location || "—"} />
            {!hasClasses && (<>
              <Info icon={<Coins size={14} />} label={t("entry_fee")} value={tour.entry_fee > 0 ? `${tour.entry_fee} ${tour.currency}` : t("free")} />
              <Info icon={<Users size={14} />} label={t("max_players")} value={`${tour.registrations?.length || 0}/${tour.max_players}`} />
            </>)}
            <Info icon={<Trophy size={14} />} label="Format" value={tour.format} />
          </div>

          {/* Per-class panel — primary registration UX when classes exist */}
          {hasClasses && (
            <div className="mt-8 space-y-3" data-testid="tour-classes-list">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-xl font-bold flex items-center gap-2">
                  <Trophy size={18} className="text-[#FFD60A]" /> Classes
                </h3>
                {tour.multi_class_discount_pct > 0 && (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#00E676]/10 text-[#00E676]" data-testid="tour-multi-discount-chip">
                    {tour.multi_class_discount_pct}% off ≥ 2 classes
                  </span>
                )}
              </div>
              {tour.classes.map((cl, idx) => {
                const myReg = myRegByClass[cl.id];
                const myStatus = myReg?.status;  // confirmed | waitlisted | undefined
                const myPosition = myReg?.waitlist_position;
                return (
                  <div key={cl.id} data-testid={`tour-class-row-${idx}`}
                    className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium flex items-center gap-2 flex-wrap">
                          <span>{cl.title}</span>
                          {cl.sport_id && cl.sport_id !== tour.sport_id && (
                            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#007AFF]/15 text-[#007AFF] border border-[#007AFF]/30 flex items-center gap-1" data-testid={`tour-class-${idx}-sport-chip`}>
                              <Trophy size={9} /> {cl.sport?.name || "Other sport"}
                            </span>
                          )}
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white/5 text-zinc-400">{cl.gender}</span>
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white/5 text-zinc-400">{cl.format?.replace(/_/g, " ")}</span>
                          {(cl.age_min || cl.age_max) && (
                            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white/5 text-zinc-400">
                              age {cl.age_min || 0}{cl.age_max ? `–${cl.age_max}` : "+"}
                            </span>
                          )}
                          {(cl.skill_rating_min != null || cl.skill_rating_max != null) && (
                            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#007AFF]/10 text-[#007AFF]">
                              {cl.skill_rating_system || "rating"} {cl.skill_rating_min != null ? cl.skill_rating_min : ""}{cl.skill_rating_max != null ? `–${cl.skill_rating_max}` : (cl.skill_rating_min != null ? "+" : "")}
                            </span>
                          )}
                        </div>
                        {cl.notes && <div className="text-xs text-zinc-500 mt-1">{cl.notes}</div>}
                        <div className="text-xs text-zinc-500 mt-2 flex items-center gap-3 flex-wrap">
                          <span className="flex items-center gap-1"><Users size={11} /> {cl.registered_count || 0} / {cl.capacity} players</span>
                          <span className="flex items-center gap-1"><Coins size={11} /> {Number(cl.price) > 0 ? `${cl.price} ${cl.currency}` : "Free"}</span>
                          {cl.is_full && <span className="text-[#FF3B30] font-bold">FULL</span>}
                          {cl.waitlist_count > 0 && (
                            <span className="text-[#FFD60A]" data-testid={`tour-class-${idx}-waitlist-count`}>
                              {cl.waitlist_count} waitlisted
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0 flex items-center gap-1.5">
                        {myStatus === "confirmed" && (
                          <>
                            <span className="pp-chip pp-chip-active text-xs" data-testid={`tour-class-${idx}-joined`}>{t("joined")}</span>
                            <button
                              onClick={() => unregister(cl.id)}
                              data-testid={`tour-class-${idx}-unregister`}
                              title="Unregister"
                              className="px-2.5 py-1.5 rounded-lg text-xs border border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10"
                            >
                              ✕
                            </button>
                          </>
                        )}
                        {myStatus === "waitlisted" && (
                          <>
                            <span className="text-xs px-2.5 py-1.5 rounded-lg bg-[#FFD60A]/10 text-[#FFD60A] flex items-center gap-1" data-testid={`tour-class-${idx}-waitlist-pos`}>
                              Waitlist · #{myPosition}
                            </span>
                            <button
                              onClick={() => unregister(cl.id)}
                              data-testid={`tour-class-${idx}-leave-waitlist`}
                              title="Leave waitlist"
                              className="px-2.5 py-1.5 rounded-lg text-xs border border-white/10 text-zinc-400 hover:bg-white/5"
                            >
                              ✕
                            </button>
                          </>
                        )}
                        {!myStatus && !cl.is_full && (
                          <button
                            onClick={() => reg(cl.id)}
                            data-testid={`tour-class-${idx}-register`}
                            className="pp-btn-primary px-4 py-2 rounded-lg text-xs"
                          >
                            {Number(cl.price) > 0 ? `Register · ${cl.price} ${cl.currency}` : t("register_now")}
                          </button>
                        )}
                        {!myStatus && cl.is_full && (
                          <button
                            onClick={() => reg(cl.id, { waitlist: true })}
                            data-testid={`tour-class-${idx}-join-waitlist`}
                            className="px-4 py-2 rounded-lg text-xs border border-[#FFD60A]/40 bg-[#FFD60A]/10 text-[#FFD60A] hover:bg-[#FFD60A]/20"
                          >
                            Join waitlist
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-8 flex gap-3 flex-wrap">
            {!hasClasses && (
              isReg ? (
                <span className="pp-chip pp-chip-active" data-testid="tour-registered">{t("joined")}</span>
              ) : (
                <button data-testid="tour-register-btn" onClick={() => reg(null)} className="pp-btn-primary px-6 py-3 rounded-xl">{t("register_now")}</button>
              )
            )}
            <AddToCalendarButton
              kind="tournament"
              id={id}
              start={tour.start_date}
              end={tour.end_date}
              title={tour.title}
              location={tour.location}
              description={tour.description}
              testIdSuffix="tour"
            />
            {isOrganizer && !tour.bracket && (
              <button data-testid="tour-generate-bracket" onClick={generateBracket} className="pp-btn-secondary px-6 py-3 rounded-xl flex items-center gap-2"><Trophy size={14} /> Generate bracket</button>
            )}
            {isOrganizer && (
              <>
                <InvitePlayersDialog eventKind="tournament" eventId={id} onInvited={fetchTour} />
                <EventShareDialog eventKind="tournament" eventId={id} eventTitle={tour.title} />
              </>
            )}
          </div>
        </div>
        <aside className="pp-card p-6">
          <h3 className="font-heading text-xl font-bold mb-4">Registered ({tour.registrations?.length || 0})</h3>
          <div className="mb-4">
            <AttendanceWidget eventKind="tournament" eventId={id} />
          </div>
          <div className="space-y-3 max-h-[400px] overflow-auto" data-testid="tour-registrations">
            {tour.registrations?.map((r) => (
              <div key={r.id} className="flex items-center gap-3">
                <Avatar className="h-9 w-9 border border-white/10"><AvatarFallback className="bg-[#007AFF] text-white">{r.user?.first_name?.[0] || "?"}</AvatarFallback></Avatar>
                <div className="text-sm">
                  <div className="font-medium">{r.team_name || `${r.user?.first_name || ""}`}</div>
                  <div className="text-xs text-zinc-500">{r.status}</div>
                </div>
              </div>
            ))}
            {(tour.registrations?.length || 0) === 0 && <div className="text-zinc-500 text-sm">No registrations yet.</div>}
          </div>
        </aside>
      </div>

      {tour.bracket && (
        <section className="mt-10">
          <h2 className="font-heading text-2xl font-bold mb-4 flex items-center gap-2"><Trophy size={20} className="text-[#00E676]" /> Bracket</h2>
          <Bracket
            bracket={tour.bracket}
            playersById={playersById}
            onRecord={isOrganizer ? (ri, mi, m) => setOpenDlg({ ri, mi, a: m.a, b: m.b }) : null}
          />
          <ResultDialog
            open={!!openDlg}
            onOpenChange={(v) => { if (!v) setOpenDlg(null); }}
            title={openDlg ? `${namify(openDlg.a)} vs ${namify(openDlg.b)}` : ""}
            playerA={{ name: openDlg ? namify(openDlg.a) : "" }}
            playerB={{ name: openDlg ? namify(openDlg.b) : "" }}
            onSubmit={({ score_a, score_b }) => {
              if (openDlg) recordBracket(openDlg.ri, openDlg.mi, score_a, score_b);
              setOpenDlg(null);
            }}
            testIdPrefix="tour-result"
          />
        </section>
      )}

      {isGolf && (
        <section className="mt-10 space-y-4" data-testid="tour-golf-section">
          {tour.golf?.format === "ryder_cup" && (
            <RyderCupRounds
              eventId={id}
              eventKind="tournament"
              isOrganizer={isOrganizer}
              registrants={tour.registrations || []}
            />
          )}
          {tour.golf?.format !== "ryder_cup" && isReg && (
            <GolfScorecard event={tour} eventKind="tournament" onSaved={() => setScoreSignal((s) => s + 1)} />
          )}
          {tour.golf?.format !== "ryder_cup" && (
            <GolfLeaderboard eventId={id} eventKind="tournament" refreshSignal={scoreSignal} />
          )}
        </section>
      )}
    </AppLayout>
  );
}

function Info({ icon, label, value }) {
  return <div className="pp-card p-3"><div className="text-xs text-zinc-500 uppercase tracking-wider flex items-center gap-2">{icon} {label}</div><div className="font-medium mt-1">{value}</div></div>;
}
